import { useLayoutEffect, useRef, useState } from 'react';

import { Icon } from '../components/Icon.js';
import {
  formatDuration,
  formatWeekdayNarrow,
  fromDateTimeInput,
  toDateInput,
  toTimeInput,
} from '../util/format.js';
import {
  addDays,
  daysOf,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  stepAnchor,
} from './calendar-rules.js';
import { formatCalendarPeriod } from './period.js';
import styles from './DateTimeFields.module.css';

/*
 * The form's date and time fields.
 *
 * Written rather than left to `<input type="date|time">`, and not only because
 * the browser's are dated: Chrome's calendar opens its weeks on Sunday, which is
 * the one thing this product has decided they do not do (see `startOfWeek`). A
 * form that contradicts the grid beside it about where a week begins is worse
 * than a plain text field. The rest follows: these wear the app's accent rather
 * than the browser's blue, and the end-time list can say how long the window
 * would run, which no native control can.
 */

/**
 * The grain the time list offers. A Chat Day is a deliberate block of time, and
 * 13:15 is a real time to open one — so quarter hours, not the half hour a click
 * on the grid would snap to.
 */
const PICKER_MINUTES = 15;
const MINUTES_PER_DAY = 24 * 60;

/** Rough height of an open popover, for deciding whether it opens upward. */
const POPOVER_HEIGHT = 300;

/**
 * Every time the picker can offer, built once.
 *
 * 96 entries, each allocating a Date, on every render of a fully controlled form
 * — which is every keystroke in the title or the description, for two fields,
 * whether or not either is open. The list is the same 96 strings forever.
 */
const TIMES: { time: string; minutes: number }[] = Array.from(
  { length: MINUTES_PER_DAY / PICKER_MINUTES },
  (_, index) => ({ time: fromMinutes(index * PICKER_MINUTES), minutes: index * PICKER_MINUTES }),
);

/** The ones that could end a window starting at `after`. */
function timesAfter(after: number | null): typeof TIMES {
  return after === null ? TIMES : TIMES.filter((option) => option.minutes > after);
}

/**
 * Whether a popover has to open upward to stay on screen. Measured from the
 * field at the moment it opens: the modal scrolls, so a field can be anywhere
 * down the viewport.
 */
function useOpensUp(open: boolean) {
  const fieldRef = useRef<HTMLDivElement>(null);
  const [up, setUp] = useState(false);

  useLayoutEffect(() => {
    const field = fieldRef.current;
    if (!open || !field) return;
    const { bottom } = field.getBoundingClientRect();
    setUp(window.innerHeight - bottom < POPOVER_HEIGHT);
  }, [open]);

  return { fieldRef, up };
}

interface DateFieldProps {
  /** `YYYY-MM-DD`, the same string an `<input type="date">` would carry. */
  value: string;
  onChange: (value: string) => void;
}

export function DateField({ value, onChange }: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const { fieldRef, up } = useOpensUp(open);

  const selected = value ? fromDateTimeInput(value, '00:00') : startOfDay(new Date());
  const valid = !Number.isNaN(selected.getTime());
  // Which month the grid is showing, which is not which day is selected: paging
  // to December to look is not the same as booking something in December.
  const [month, setMonth] = useState(() => startOfMonth(valid ? selected : new Date()));
  const today = startOfDay(new Date());

  const pick = (day: Date) => {
    onChange(toDateInput(day));
    setOpen(false);
  };

  return (
    <div className={styles.field} ref={fieldRef}>
      <button
        type="button"
        className={open ? styles.triggerOpen : styles.trigger}
        aria-label="Date"
        onClick={() => {
          if (valid) setMonth(startOfMonth(selected));
          setOpen((current) => !current);
        }}
        // The popover's own rows select on mousedown and put focus back here, so
        // a blur means the employer genuinely left the field.
        onBlur={() => setOpen(false)}
      >
        {valid ? formatCalendarPeriod('day', selected) : 'Pick a date'}
      </button>

      {open && (
        <div className={up ? styles.popoverUp : styles.popover}>
          <div className={styles.monthBar}>
            <button
              type="button"
              className={styles.monthStep}
              aria-label="Previous month"
              onMouseDown={(event) => {
                event.preventDefault();
                setMonth((current) => stepAnchor('month', current, -1));
              }}
            >
              <Icon name="chevronLeft" size={18} />
            </button>
            <span className={styles.monthName}>{formatCalendarPeriod('month', month)}</span>
            <button
              type="button"
              className={styles.monthStep}
              aria-label="Next month"
              onMouseDown={(event) => {
                event.preventDefault();
                setMonth((current) => stepAnchor('month', current, 1));
              }}
            >
              <Icon name="chevronRight" size={18} />
            </button>
          </div>

          {/* The same Monday-led days the calendar itself lays out, from the same
              function — so the week in this popover is the week in the grid
              behind it. */}
          <div className={styles.grid}>
            {daysOf('week', month).map((day) => (
              <span key={day.getDay()} className={styles.gridHead}>
                {formatWeekdayNarrow(day)}
              </span>
            ))}

            {daysOf('month', month).map((day) => (
              <button
                key={day.toISOString()}
                type="button"
                className={[
                  isSameDay(day, selected) ? styles.dayPicked : styles.day,
                  !isSameMonth(day, month) && styles.dayOutside,
                  isSameDay(day, today) && !isSameDay(day, selected) && styles.dayToday,
                ]
                  .filter(Boolean)
                  .join(' ')}
                onMouseDown={(event) => {
                  event.preventDefault();
                  pick(day);
                }}
              >
                {day.getDate()}
              </button>
            ))}
          </div>

          <div className={styles.popoverFoot}>
            <button
              type="button"
              className={styles.footAction}
              onMouseDown={(event) => {
                event.preventDefault();
                pick(today);
              }}
            >
              Today
            </button>
            <button
              type="button"
              className={styles.footAction}
              onMouseDown={(event) => {
                event.preventDefault();
                pick(addDays(today, 1));
              }}
            >
              Tomorrow
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface TimeFieldProps {
  /** `HH:mm`, the same string an `<input type="time">` would carry. */
  value: string;
  onChange: (value: string) => void;
  label: string;
  /**
   * The start, when this is the end field: the list then offers only times after
   * it, each labelled with how long the window would run. An end before its
   * start is not a validation error to report, it is an option not worth
   * offering.
   */
  after?: string;
}

export function TimeField({ value, onChange, label, after }: TimeFieldProps) {
  const [open, setOpen] = useState(false);
  const { fieldRef, up } = useOpensUp(open);
  const pickedRef = useRef<HTMLButtonElement>(null);

  // Opens on what is already chosen rather than at midnight, which is eight
  // hours of scrolling away from any time anyone books.
  useLayoutEffect(() => {
    if (open) pickedRef.current?.scrollIntoView({ block: 'center' });
  }, [open]);

  const startMinutes = after ? toMinutes(after) : null;

  return (
    <div className={styles.field} ref={fieldRef}>
      <button
        type="button"
        className={open ? styles.triggerOpen : styles.trigger}
        aria-label={label}
        onClick={() => setOpen((current) => !current)}
        onBlur={() => setOpen(false)}
      >
        {value || '--:--'}
      </button>

      {open && (
        <div className={up ? styles.timesUp : styles.times}>
          {timesAfter(startMinutes).map((option) => (
            <button
              key={option.time}
              ref={option.time === value ? pickedRef : undefined}
              type="button"
              className={option.time === value ? styles.timePicked : styles.time}
              onMouseDown={(event) => {
                event.preventDefault();
                onChange(option.time);
                setOpen(false);
              }}
            >
              {option.time}
              {startMinutes !== null && (
                <span className={styles.timeHint}>
                  {formatDuration(option.minutes - startMinutes)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function toMinutes(time: string): number {
  const [hour, minute] = time.split(':').map(Number);
  return (hour ?? 0) * 60 + (minute ?? 0);
}

function fromMinutes(minutes: number): string {
  // Through the same serialiser the rest of the form uses, so a time this picker
  // produces is byte-identical to one `newDraft` produced.
  const day = startOfDay(new Date());
  day.setMinutes(minutes);
  return toTimeInput(day);
}
